class AnimationCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
    this.isPreloading = false;
  }

  // Animation URLs mapping
  getAnimationUrls() {
    return {
      'Real Look': 'https://cdn.app.flashy.ai/static-assets/real+look+(1).json',
      'Hero Focus': 'https://cdn.app.flashy.ai/static-assets/Hero+Focus.json',
      'Smooth Entry': 'https://cdn.app.flashy.ai/static-assets/smooth+entry.json',
      'Portrait Pan': 'https://cdn.app.flashy.ai/static-assets/Portrait+Pan.json',
      'Gimbal Glide': 'https://cdn.app.flashy.ai/static-assets/gimbalglide.json',
      'Turntable 360': 'https://cdn.app.flashy.ai/static-assets/turn+table+360.json'
    };
  }

  // Fetch and cache a single animation
  async loadAnimation(movementId) {
    // Return cached animation if available
    if (this.cache.has(movementId)) {
      return this.cache.get(movementId);
    }

    // Return existing loading promise if already in progress
    if (this.loadingPromises.has(movementId)) {
      return this.loadingPromises.get(movementId);
    }

    const animationUrls = this.getAnimationUrls();
    const url = animationUrls[movementId];

    if (!url) {
      console.warn(`No animation URL found for movement: ${movementId}`);
      return null;
    }

    // Create loading promise
    const loadingPromise = this.fetchAnimationData(url, movementId);
    this.loadingPromises.set(movementId, loadingPromise);

    try {
      const animationData = await loadingPromise;
      this.cache.set(movementId, animationData);
      this.loadingPromises.delete(movementId);
      return animationData;
    } catch (error) {
      console.error(`Failed to load animation for ${movementId}:`, error);
      this.loadingPromises.delete(movementId);
      return null;
    }
  }

  // Fetch animation data from URL
  async fetchAnimationData(url, movementId) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const animationData = await response.json();

      return animationData;
    } catch (error) {
      console.error(`Failed to fetch animation ${movementId}:`, error);
      throw error;
    }
  }

  // Preload all animations
  async preloadAllAnimations() {
    if (this.isPreloading) {
      return;
    }

    this.isPreloading = true;

    const animationUrls = this.getAnimationUrls();
    const movements = Object.keys(animationUrls);

    try {
      // Load all animations in parallel
      const loadPromises = movements.map(movementId =>
        this.loadAnimation(movementId).catch(error => {
          console.error(`Failed to preload ${movementId}:`, error);
          return null; // Don't fail the entire preload process
        })
      );

      const results = await Promise.allSettled(loadPromises);

      const successful = results.filter(result => result.status === 'fulfilled' && result.value !== null).length;
      const failed = results.length - successful;
    } catch (error) {
      console.error('Animation preloading failed:', error);
    } finally {
      this.isPreloading = false;
    }
  }

  // Get cached animation data
  getCachedAnimation(movementId) {
    return this.cache.get(movementId) || null;
  }

  // Check if animation is cached
  isAnimationCached(movementId) {
    return this.cache.has(movementId);
  }

  // Check if animation is currently loading
  isAnimationLoading(movementId) {
    return this.loadingPromises.has(movementId);
  }

  // Get cache statistics
  getCacheStats() {
    return {
      cachedCount: this.cache.size,
      loadingCount: this.loadingPromises.size,
      isPreloading: this.isPreloading,
      cachedAnimations: Array.from(this.cache.keys())
    };
  }

  // Clear cache (useful for development/testing)
  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
  }
}

// Create singleton instance
const animationCache = new AnimationCache();

export default animationCache;
